import React from 'react';
import Button from '../../components/Form/Button';
import { PaymentOrder, PaymentOrderItem } from '../../types';
import { resolveCurrencyCode } from '../../utils/currencyUtils';

interface PaymentOrderItemsRowProps {
  order: PaymentOrder;
  colSpan: number;
  onItemStatus: (order: PaymentOrder, item: PaymentOrderItem, status: 'approved' | 'purchased') => void;
}

const itemStatusCls: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  purchased: 'bg-blue-100 text-blue-800',
};

/** Expanded row showing a payment order's items with per-item approval */
const PaymentOrderItemsRow: React.FC<PaymentOrderItemsRowProps> = ({ order, colSpan, onItemStatus }) => {
  const code = resolveCurrencyCode(order.currency);
  const items = order.items || [];
  return (
    <tr className="bg-gray-50">
      <td colSpan={colSpan} className="px-6 py-4">
        <p className="text-xs font-semibold text-gray-600 uppercase mb-2">
          Items — Requisition {order.referenceNo || '-'}
        </p>
        <table className="min-w-full text-sm border border-gray-200 rounded">
          <thead className="bg-gray-100">
            <tr>
              {['#', 'Item', 'Qty', 'ROB', 'App Qty', 'Unit Price', 'Total', 'Status', 'Actions'].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {items.map((item: any, idx: number) => {
              const st = item.status || 'pending';
              return (
                <tr key={item.id || idx}>
                  <td className="px-3 py-2">{item.no || idx + 1}</td>
                  <td className="px-3 py-2 font-medium text-gray-900">{item.details || '-'}</td>
                  <td className="px-3 py-2">{item.qty ?? '-'}</td>
                  <td className="px-3 py-2">{item.rob || '-'}</td>
                  <td className="px-3 py-2">{item.appQty ?? '-'}</td>
                  <td className="px-3 py-2">{Number(item.unitPrice || 0).toLocaleString()} {code}</td>
                  <td className="px-3 py-2">{Number(item.total || 0).toLocaleString()} {code}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${itemStatusCls[st] || itemStatusCls.pending}`}>
                      {st.charAt(0).toUpperCase() + st.slice(1)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex space-x-2">
                      {st === 'pending' && (
                        <Button onClick={() => onItemStatus(order, item, 'approved')} variant="primary" size="sm" className="text-xs">Approve</Button>
                      )}
                      {st === 'approved' && (
                        <Button onClick={() => onItemStatus(order, item, 'purchased')} variant="success" size="sm" className="text-xs">Mark Purchased</Button>
                      )}
                      {st === 'purchased' && <span className="text-xs text-gray-400">Done</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-3 text-center text-gray-500">No items on this order</td></tr>
            )}
          </tbody>
        </table>
      </td>
    </tr>
  );
};

export default PaymentOrderItemsRow;
